import { ContainerType, ElectronicLocation, Icon, IconFormat, Record } from './models/record';
import { UntypedFormGroup } from '@angular/forms';
import { Favorite } from './state/user/favorites/models/favorites.response';
import { Rating } from './models/rating';
import { CssColorType } from './models/css-var';
import { Checkout } from './state/user/checkouts/models/checkout';
import { Action, ActionType } from './models/action';
import { Challenge } from './models/challenge';
import { NavigationEnd, Router } from '@angular/router';
import { FilterValue } from './models/filter-value';
import { Filter } from './models/filter';
import { experiencesDescriptions } from './models/experiences';
import { InfoDialogData, InfoDialogItem } from './models/info-dialog';
import { RssItem } from './models/rss';
import { User } from './models/user';
import { PlayerUrlAnnotationService } from './services/api/player-url-annotation.service';

export class Util {
  static COLLECTIONS_KEY = 'cached-collections';
  static BOOKCLUBS_KEY = 'cached-bookclubs';

  static EMAIL_PATTERN =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  static CEP_PATTERN = /^[0-9]{5}-[0-9]{3}$/;
  static BOT_PATTERN =
    /googlebot|adsbot\-google|Feedfetcher\-Google|bingbot|yandex|baiduspider|Facebot|facebookexternalhit|twitterbot|rogerbot|linkedinbot|embedly|quora link preview|showyoubot|outbrain|pinterest|slackbot|vkShare|W3C_Validator/i;

  static QUOTED_TERMS_PATTERN = /(.+?)("[^"]+")/g;
  static ALPHANUMERIC_PATTERN = /^[a-zA-Z0-9]*$/;
  static PASSWORD_PATTERN = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z]{8,}$/;
  static NUMBERS_ONLY_PATTERN = /[0-9]/;
  static NIF_CHARS = 'TRWAGMYFPDXBNJZSQVHLCKE';
  static RUT_FACTORS = [3, 2, 7, 6, 5, 4, 3, 2];
  static RUT_PATTERN = /^\d{7,8}[0-9kK]$/;

  static ALLFIELDS = 'allfields_txt';
  static AUTHOR_FIELD = 'author';
  static TITLE_FIELD = 'title';
  static PUBLISHER_FIELD = 'publisher';
  static SUBJECT_FIELD = 'subject';
  static API_SEARCH_FIELDS = [
    Util.ALLFIELDS,
    Util.AUTHOR_FIELD,
    Util.TITLE_FIELD,
    Util.PUBLISHER_FIELD,
    Util.SUBJECT_FIELD,
  ];

  static FORMAT_FILTER = 'format_facet_ss';
  static OPL_FORMAT_FILTER = 'formatoDigital_ss';
  static LANGUAGE_FILTERS = ['idioma_facet_ss', 'idioma_original_facet_ss', 'marc041$a_lit'];
  static SUBJECT_FILTER = 'materia_facet_ss';
  static PPU_FILTER = 'ppu_facet_ss';
  static THEMA_FILTER = 'thema_facet_ss';
  static COLLECTION_FILTER = 'collection_facet_ss';
  static PUBLIC_FILTER = 'public_facet_ss';
  static MAX_FILTER_VALUES = 5;
  static MAX_EXPANDED_VALUES = 15;
  static DEFAULT_CUSTOMER_TYPE = {
    dbValue: 'Default',
    menuOrder: [
      'HELP',
      'CHECKOUTS',
      'HOLDS',
      'FAVORITES',
      'LISTS',
      'SUGGESTIONS',
      'DESIDERATA',
      'HISTORY',
      'VISUALIZATIONS',
      'SEARCH',
      'STATS',
      'EVENTS',
      'CHALLENGES',
      'DEVICES',
      'NOTIFICATIONS',
      'BOOKCLUBS',
      'CAREER_PLANS',
      'DETAILS',
      'INTERESTS',
    ],
  };
  static DEFAULT_SOLUTION_TYPE = 'Default';
  static SOLUTION_DEFAULT = 'Default';
  static SOLUTION_LIMITED = 'Limited';
  static SOLUTION_UNLIMITED = 'Unlimited';
  static ORIGINAL_FORMATS = [
    'PHYSICAL',
    'VACIADO_ARTICULOS',
    'PUBLICACIONES_SERIADAS',
    'FICHEROS_INFORMATICOS',
    'MAPAS',
    'MATERIALES_VISUALES',
    'MUSICA1',
    'MUSICA2',
    'RECURSOS_CONTINUOS',
    'ARTICULOS',
    'CLASIFICACION',
    'COMUNIDAD',
    'BK_MATERIAL_IMPRESO_TEXTUAL',
    'CR_EJEMPLARES',
    'BK_MATERIAL_MANUSCRITO_TEXTUAL',
    'MP_MATERIAL_CARTOGRAFICO',
    'MP_MATERIAL_CARTOGRAFICO_MANUSCRITO',
    'MU_MUSICA_IMPRESA_CON_NOTACION',
    'MU_MUSICA_MANUSCRITA',
    'MU_GRABACION_SONORA_NO_MUSICAL',
    'VM_MEDIO_PROYECTABLE',
  ];
  static DEFAULT_LANGUAGE_CODE_FOR_CAROUSELS = 'df';

  public static getOrigin(): string {
    if (!window.location.origin) {
      return (
        window.location.protocol +
        '//' +
        window.location.hostname +
        (window.location.port ? ':' + window.location.port : '')
      );
    }

    return window.location.origin;
  }

  public static checkPasswords(group: UntypedFormGroup) {
    const newPassword = group.controls.newPassword.value;
    const confirmNewPassword = group.controls.confirmNewPassword.value;

    if (newPassword !== confirmNewPassword) {
      group.controls.confirmNewPassword?.setErrors({ notEquals: true });
    }
    return newPassword === confirmNewPassword ? null : { notEquals: true };
  }

  public static isBot(): boolean {
    const userAgent = window.navigator.userAgent;
    return Util.BOT_PATTERN.test(userAgent);
  }

  public static prepareRecord(record: Record): Record {
    Object.keys(record).forEach((key) => {
      if (typeof record[key] === 'string') {
        record[key] = record[key].trim();
      }
    });

    record.coverImageUrl = this.resolveCover(record);
    record.originalImageUrl = this.resolveCover(record, true);
    record.icon = this.resolveIcon(record, record.iconId);
    record.availableIssueDates = record.availableIssueDates || [];
    record.electronicLocation = this.resolveElectronicLocations(record.electronicLocation);

    if (record.formats) {
      record.formats = record.formats.reverse();
    }

    if (record.containerData != null && record.containerData.containerType.valueOf() === ContainerType.RSS.valueOf()) {
      if (record.containerData.lastRssItems) {
        record.containerData.lastRssItems.forEach((rssItem) => this.prepareRssItem(rssItem));
      }
    }

    return record;
  }

  public static prepareRssItem(rssItem: RssItem) {
    if (!rssItem.icon) {
      rssItem.icon = Util.resolveIcon(rssItem.resourceType);
    }
  }

  public static moveSelectedCoverToTop(covers: string[], index: number): string[] {
    covers.splice(0, 0, covers.splice(index, 1)[0]);
    return covers.filter((c) => c !== null);
  }

  public static buildTopicFacetQuery(facet: string): object {
    return { facets: `${Util.SUBJECT_FILTER}:"${facet}"` };
  }

  public static buildPpuFacetQuery(): object {
    return { facets: `${Util.PPU_FILTER}:"simultaneousUse"` };
  }

  public static prepareFavorite(favorite: Favorite): Favorite {
    favorite.coverURL = favorite.coverURL || '/assets/cover.jpg';
    favorite.date = new Date(favorite.date);
    return favorite;
  }

  public static resolveIcon(element: Record | string, iconId?: string): Icon {
    if (typeof element === 'string') {
      element = this.createDummyRecord(element);
    }

    const specialFormat =
      (element.specialFormat === undefined || element.specialFormat === null) ? '' : element.specialFormat.toUpperCase().replace(' ', '_');
    switch (specialFormat) {
      case 'PODCAST':
        return {
          icon: IconFormat.FORMAT_PODCAST,
          label: 'formats.podcast',
          className: 'odilo-icon',
        };
      case 'SUMMARY':
        return {
          icon: IconFormat.FORMAT_SUMMARY,
          label: 'formats.summary',
          className: 'odilo-icon',
        };
      case 'ARTICLE':
        return {
          icon: IconFormat.FORMAT_ARTICLE,
          label: 'formats.article',
          className: 'odilo-icon',
        };
      case 'DATABASE':
        return {
          icon: IconFormat.FORMAT_DATABASE,
          label: 'formats.database',
          className: 'odilo-icon',
        };
      case 'LEARNING_PATH':
        return {
          icon: IconFormat.FORMAT_LEARNING_PATH,
          label: 'Learning Path',
          className: 'le-icon',
        };
      case 'MUSIC_SHEET':
        return {
          icon: IconFormat.FORMAT_MUSIC_SHEET,
          label: 'formats.musicsheet',
          className: 'odilo-icon',
        };
      case 'FILM':
        return {
          icon: IconFormat.FORMAT_FILM,
          label: 'formats.film',
          className: 'odilo-icon',
        };
      case 'ALBUM':
        return {
          icon: IconFormat.FORMAT_ALBUM,
          label: 'formats.album',
          className: 'odilo-icon',
        };
      case 'LEARNING_PILL':
        return { icon: IconFormat.FORMAT_LEARNING_PILL, label: 'formats.learningPill', className: 'odilo-icon' };
      case 'MAGAZINE':
        return { icon: IconFormat.FORMAT_MAGAZINE, label: 'formats.magazine', className: 'odilo-icon' };
    }

    if (element.specialFormat && element.iconId) {
      return {
        icon: iconId,
        label: element.specialFormat,
        className: 'le-icon',
      };
    }

    if (element.externalLink && (element.resourceTypes === undefined || element.resourceTypes.length === 0)) {
      return {
        icon: IconFormat.FORMAT_EXTERNAL,
        label: 'formats.external',
        className: 'odilo-icon',
      };
    } else if (element.attachedResource && Object.keys(element.attachedResource).length) {
      return {
        icon: IconFormat.FORMAT_DOWNLOAD,
        label: 'common.download',
        className: 'odilo-icon',
      };
    }

    if (element.resourceTypes == null || element.resourceTypes.length === 0) {
      return {
        icon: IconFormat.FORMAT_UNDEFINED,
        label: '',
        className: 'odilo-icon',
      };
    }

    // TODO: meh
    if (element.resourceTypes) {
      for (const resourceType of element.resourceTypes) {
        const type = resourceType.toUpperCase().replace(' ', '_');
        if (type.includes('NEWSPAPER')) {
          return {
            icon: IconFormat.FORMAT_NEWSPAPER,
            label: 'formats.newspaper',
            className: 'odilo-icon',
          };
        } else if (type.includes('MAGAZINE')) {
          return {
            icon: IconFormat.FORMAT_MAGAZINE,
            label: 'formats.magazine',
            className: 'odilo-icon',
          };
        } else if (type.includes('STREAMING') || type.includes('EBOOK') || type.includes('_CB_')) {
          return {
            icon: IconFormat.FORMAT_EBOOK,
            label: 'formats.ebook',
            className: 'odilo-icon',
          };
        } else if (type.includes('AUDIO') || type.includes('FINDAWAY') || type.includes('LUISTERHUIS')) {
          return {
            icon: IconFormat.FORMAT_AUDIO,
            label: 'formats.audio',
            className: 'odilo-icon',
          };
        } else if (type.includes('VIDEO')) {
          return {
            icon: IconFormat.FORMAT_VIDEO,
            label: 'formats.video',
            className: 'odilo-icon',
          };
        } else if (type.includes('SCORM')) {
          return {
            icon: IconFormat.FORMAT_EDUCATION,
            label: 'formats.scorm',
            className: 'odilo-icon',
          };
        } else if (type.includes('IMAGE')) {
          return {
            icon: IconFormat.FORMAT_IMAGE,
            label: 'formats.image',
            className: 'odilo-icon',
          };
        } else if (type.includes('PODCAST')) {
          return {
            icon: IconFormat.FORMAT_PODCAST,
            label: 'formats.podcast',
            className: 'odilo-icon',
          };
        } else if (type.includes('SUMMARY')) {
          return {
            icon: IconFormat.FORMAT_SUMMARY,
            label: 'formats.summary',
            className: 'odilo-icon',

          };
        } else if (type.includes('ARTICLE')) {
          return {
            icon: IconFormat.FORMAT_ARTICLE,
            label: 'formats.article',
            className: 'odilo-icon',
          };
        } else if (type.includes('DATABASE')) {
          return {
            icon: IconFormat.FORMAT_DATABASE,
            label: 'formats.database',
            className: 'odilo-icon',
          };
        } else if (type.includes('BOOKCLUB')) {
          return {
            icon: IconFormat.FORMAT_BOOKCLUB,
            label: 'Book Club',
            className: 'le-icon',
          };
        } else if (type.includes('LEARNING_CLUB')) {
          return {
            icon: IconFormat.FORMAT_LEARNING_CLUB,
            label: 'Learning Club',
            className: 'le-icon',
          };
        } else if (type.includes('CLASSROOM')) {
          return {
            icon: IconFormat.FORMAT_CLASSROOM,
            label: 'Classroom',
            className: 'le-icon',
          };
        } else if (type.includes('READING_PLAN')) {
          return {
            icon: IconFormat.FORMAT_READING_PLAN,
            label: 'Reading Plan',
            className: 'le-icon',
          };
        } else if (type.includes('LEARNING_PATH')) {
          return {
            icon: IconFormat.FORMAT_LEARNING_PATH,
            label: 'Learning Path',
            className: 'le-icon',
          };
        } else if (type.includes('MUSIC_SHEET')) {
          return {
            icon: IconFormat.FORMAT_MUSIC_SHEET,
            label: 'formats.musicsheet',
            className: 'odilo-icon',
          };
        } else if (type.includes('FILM')) {
          return {
            icon: IconFormat.FORMAT_FILM,
            label: 'formats.film',
            className: 'odilo-icon',
          };
        } else if (type.includes('ALBUM')) {
          return {
            icon: IconFormat.FORMAT_ALBUM,
            label: 'formats.album',
            className: 'odilo-icon',
          };
        } else if (type.includes('LEARNING_PILL')) {
          return {
            icon: IconFormat.FORMAT_LEARNING_PILL,
            label: 'formats.learningPill',
            className: 'odilo-icon',
          };
        } else if (type.includes('UNDEFINED')) {
          return {
            icon: IconFormat.FORMAT_UNDEFINED,
            label: 'formats.undefined',
            className: 'odilo-icon',
          };
        } else if (type.includes('ALL') && !type.includes('CHALLENGE')) {
          return {
            icon: IconFormat.FORMAT_ALL,
            label: 'formats.all',
            className: 'odilo-icon',
          };
        } else if (type.includes('TIPO_FISICO')) {
          const physicalFormat = element.physicalFormat ? element.physicalFormat.toUpperCase().replace(' ', '_') : null;
          const physicalFormatLabel = physicalFormat ? 'formats.physical.' + physicalFormat.toLowerCase() : null;
          if (physicalFormat === 'VACIADO_ARTICULOS') {
            return {
              icon: IconFormat.FORMAT_VACIADO_ARTICULOS,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'PUBLICACIONES_SERIADAS') {
            return {
              icon: IconFormat.FORMAT_PUBLICACIONES_SERIADAS,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'FICHEROS_INFORMATICOS') {
            return {
              icon: IconFormat.FORMAT_FICHEROS_INFORMATICOS,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MAPAS') {
            return {
              icon: IconFormat.FORMAT_MAPAS,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MATERIALES_VISUALES') {
            return {
              icon: IconFormat.FORMAT_MATERIALES_VISUALES,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MUSICA1') {
            return {
              icon: IconFormat.FORMAT_MUSICA1,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MUSICA2') {
            return {
              icon: IconFormat.FORMAT_MUSICA2,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'RECURSOS_CONTINUOS') {
            return {
              icon: IconFormat.FORMAT_RECURSOS_CONTINUOS,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'ARTICULOS') {
            return {
              icon: IconFormat.FORMAT_ARTICULOS,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'CLASIFICACION') {
            return {
              icon: IconFormat.FORMAT_CLASIFICACION,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'COMUNIDAD') {
            return {
              icon: IconFormat.FORMAT_COMUNIDAD,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'BK_MATERIAL_IMPRESO_TEXTUAL') {
            return {
              icon: IconFormat.FORMAT_BK_MATERIAL_IMPRESO_TEXTUAL,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'CR_EJEMPLARES') {
            return {
              icon: IconFormat.FORMAT_CR_EJEMPLARES,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'BK_MATERIAL_MANUSCRITO_TEXTUAL') {
            return {
              icon: IconFormat.FORMAT_BK_MATERIAL_MANUSCRITO_TEXTUAL,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MP_MATERIAL_CARTOGRAFICO') {
            return {
              icon: IconFormat.FORMAT_MP_MATERIAL_CARTOGRAFICO,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MP_MATERIAL_CARTOGRAFICO_MANUSCRITO') {
            return {
              icon: IconFormat.FORMAT_MP_MATERIAL_CARTOGRAFICO_MANUSCRITO,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MU_MUSICA_IMPRESA_CON_NOTACION') {
            return {
              icon: IconFormat.FORMAT_MU_MUSICA_IMPRESA_CON_NOTACION,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MU_MUSICA_MANUSCRITA') {
            return {
              icon: IconFormat.FORMAT_MU_MUSICA_MANUSCRITA,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'MU_GRABACION_SONORA_NO_MUSICAL') {
            return {
              icon: IconFormat.FORMAT_MU_GRABACION_SONORA_NO_MUSICAL,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else if (physicalFormat === 'VM_MEDIO_PROYECTABLE') {
            return {
              icon: IconFormat.FORMAT_VM_MEDIO_PROYECTABLE,
              label: physicalFormatLabel,
              className: 'odilo-icon',
            };
          } else {
            return {
              icon: IconFormat.FORMAT_PHYSICAL,
              label: 'formats.physical.physical',
              className: 'odilo-icon',
            };
          }
        } else if (type.includes('PHYSICAL')) {
          return {
            icon: IconFormat.FORMAT_PHYSICAL,
            label: 'formats.physical.physical',
            className: 'odilo-icon',
          };
        } else if (type.includes('VACIADO_ARTICULOS')) {
          return {
            icon: IconFormat.FORMAT_VACIADO_ARTICULOS,
            label: 'formats.physical.vaciado_articulos',
            className: 'odilo-icon',
          };
        } else if (type.includes('PUBLICACIONES_SERIADAS')) {
          return {
            icon: IconFormat.FORMAT_PUBLICACIONES_SERIADAS,
            label: 'formats.physical.publicaciones_seriadas',
            className: 'odilo-icon',
          };
        } else if (type.includes('FICHEROS_INFORMATICOS')) {
          return {
            icon: IconFormat.FORMAT_FICHEROS_INFORMATICOS,
            label: 'formats.physical.ficheros_informaticos',
            className: 'odilo-icon',
          };
        } else if (type.includes('MAPAS')) {
          return {
            icon: IconFormat.FORMAT_MAPAS,
            label: 'formats.physical.mapas',
            className: 'odilo-icon',
          };
        } else if (type.includes('MATERIALES_VISUALES')) {
          return {
            icon: IconFormat.FORMAT_MATERIALES_VISUALES,
            label: 'formats.physical.materiales_visuales',
            className: 'odilo-icon',
          };
        } else if (type.includes('MUSICA1')) {
          return {
            icon: IconFormat.FORMAT_MUSICA1,
            label: 'formats.physical.musica1',
            className: 'odilo-icon',
          };
        } else if (type.includes('MUSICA2')) {
          return {
            icon: IconFormat.FORMAT_MUSICA2,
            label: 'formats.physical.musica1',
            className: 'odilo-icon',
          };
        } else if (type.includes('RECURSOS_CONTINUOS')) {
          return {
            icon: IconFormat.FORMAT_RECURSOS_CONTINUOS,
            label: 'formats.physical.recursos_continuos',
            className: 'odilo-icon',
          };
        } else if (type.includes('ARTICULOS')) {
          return {
            icon: IconFormat.FORMAT_ARTICULOS,
            label: 'formats.physical.articulos',
            className: 'odilo-icon',
          };
        } else if (type.includes('CLASIFICACION')) {
          return {
            icon: IconFormat.FORMAT_CLASIFICACION,
            label: 'formats.physical.clasificacion',
            className: 'odilo-icon',
          };
        } else if (type.includes('COMUNIDAD')) {
          return {
            icon: IconFormat.FORMAT_COMUNIDAD,
            label: 'formats.physical.comunidad',
            className: 'odilo-icon',
          };
        } else if (type.includes('BK_MATERIAL_IMPRESO_TEXTUAL')) {
          return {
            icon: IconFormat.FORMAT_BK_MATERIAL_IMPRESO_TEXTUAL,
            label: 'formats.physical.bk_material_manuscrito_textual',
            className: 'odilo-icon',
          };
        } else if (type.includes('CR_EJEMPLARES')) {
          return {
            icon: IconFormat.FORMAT_CR_EJEMPLARES,
            label: 'formats.physical.cr_ejemplares',
            className: 'odilo-icon',
          };
        } else if (type.includes('BK_MATERIAL_MANUSCRITO_TEXTUAL')) {
          return {
            icon: IconFormat.FORMAT_BK_MATERIAL_MANUSCRITO_TEXTUAL,
            label: 'formats.physical.bk_material_impreso_textual',
            className: 'odilo-icon',
          };
        } else if (type.includes('MP_MATERIAL_CARTOGRAFICO')) {
          return {
            icon: IconFormat.FORMAT_MP_MATERIAL_CARTOGRAFICO,
            label: 'formats.physical.mp_material_cartografico',
            className: 'odilo-icon',
          };
        } else if (type.includes('MP_MATERIAL_CARTOGRAFICO_MANUSCRITO')) {
          return {
            icon: IconFormat.FORMAT_MP_MATERIAL_CARTOGRAFICO_MANUSCRITO,
            label: 'formats.physical.mp_material_cartografico_manuscrito',
            className: 'odilo-icon',
          };
        } else if (type.includes('MU_MUSICA_IMPRESA_CON_NOTACION')) {
          return {
            icon: IconFormat.FORMAT_MU_MUSICA_IMPRESA_CON_NOTACION,
            label: 'formats.physical.mu_musica_impresa_con_notacion',
            className: 'odilo-icon',
          };
        } else if (type.includes('MU_MUSICA_MANUSCRITA')) {
          return {
            icon: IconFormat.FORMAT_MU_MUSICA_MANUSCRITA,
            label: 'formats.physical.mu_musica_manuscrita',
            className: 'odilo-icon',
          };
        } else if (type.includes('MU_GRABACION_SONORA_NO_MUSICAL')) {
          return {
            icon: IconFormat.FORMAT_MU_GRABACION_SONORA_NO_MUSICAL,
            label: 'formats.physical.mu_grabacion_sonora_no_musical',
            className: 'odilo-icon',
          };
        } else if (type.includes('VM_MEDIO_PROYECTABLE')) {
          return {
            icon: IconFormat.FORMAT_VM_MEDIO_PROYECTABLE,
            label: 'formats.physical.vm_medio_proyectable',
            className: 'odilo-icon',
          };
        } else if (type.includes('LIST')) {
          return {
            icon: IconFormat.FORMAT_LIST,
            label: 'formats.list',
            className: 'odilo-icon',
          };
        } else if (type) {
          return {
            icon: iconId,
            label: element.resourceTypes[0],
            className: 'le-icon',
          };
        }
      }
    }
    return {
      icon: IconFormat.FORMAT_UNDEFINED,
      label: '',
      className: 'odilo-icon',
    };
  }

  public static fillFormatFilterInfoMap(infoMap: Map<FilterValue, Icon>, formatFilter: Filter) {
    formatFilter.values.forEach((elem) => {
      infoMap.set(elem, Util.resolveIcon(elem.name, elem.iconId));
    });
  }

  public static parseDate(date: string): Date {
    const year = Number(date.substring(0, 4));
    const month = Number(date.substring(4, 6));
    const day = Number(date.substring(6));

    return new Date(year, month - 1, day);
  }

  public static getIssueDates(record: Record): string[] {
    return record.availableIssueDates.sort().reverse();
  }

  public static getLastIssueDate(record: Record): string {
    const issueDates = this.getIssueDates(record);

    if (issueDates) {
      return issueDates[0];
    }
  }

  public static copyToClipboard(content: string) {
    document.addEventListener('copy', (e: ClipboardEvent) => {
      e.clipboardData.setData('text/plain', content);
      e.preventDefault();
      document.removeEventListener('copy', null);
    });

    document.execCommand('copy');
  }

  public static getEmptyRating(): Rating {
    return {
      id: '0',
      mean: 0,
      totalRatings: 0,
      oneStar: 0,
      twoStars: 0,
      threeStars: 0,
      fourStars: 0,
      fiveStars: 0,
      userIdAndVoteList: [],
    };
  }

  public static groupBy<T extends any, K extends keyof T>(array: T[], key: K): Map<T[K], T[]> {
    return array.reduce(
      (objectsByKeyValue, obj) => {
        const value = obj[key];
        const groupName = String(value).split('-', 3).pop();
        objectsByKeyValue[groupName] = (objectsByKeyValue[groupName] || []).concat(obj);

        return objectsByKeyValue;
      },
      {} as Map<T[K], T[]>
    );
  }

  public static getColorType(value: string): CssColorType {
    if (value.startsWith('#')) {
      return 'hex';
    }

    if (value.startsWith('rgb')) {
      return 'rgba';
    }

    if (value.startsWith('hsl')) {
      return 'hsla';
    }

    return 'none';
  }

  public static slugify(text: string): string {
    const a = 'àáäâãåăæąçćčđďèéěėëêęğǵḧìíïîįłḿǹńňñòóöôœøṕŕřßşśšșťțùúüûǘůűūųẃẍÿýźžż·/_,:;';
    const b = 'aaaaaaaaacccddeeeeeeegghiiiiilmnnnnooooooprrsssssttuuuuuuuuuwxyyzzz------';
    const p = new RegExp(a.split('').join('|'), 'g');

    return text
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(p, (c) => b.charAt(a.indexOf(c)))
      .replace(/&/g, '-and-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }

  public static slugifyRecord(record: Record): string {
    let toSlug = record.title || '';

    if (record.subtitle) {
      toSlug += ' ' + record.subtitle;
    }

    return Util.slugify(toSlug) + '-' + record.id;
  }

  public static slugifyListRecord(record: Record): string {
    const toSlug = record.title || '';

    return Util.slugify(toSlug) + '-' + record.wishListInfo.id;
  }

  public static slugifyList(title: string, listId: string): string {
    return Util.slugify(title || '') + '-' + listId;
  }

  public static formatSubject(subject: string): string {
    return subject
      .replace(/\\p{InCombiningDiacriticalMarks}+/g, '')
      .replace(/[^a-zA-Z0-9\\.]/g, '_')
      .toLowerCase();
  }

  public static getAction(): Action | null {
    const action = localStorage.getItem('action');
    if (action) {
      return JSON.parse(action);
    }
    return null;
  }

  public static setAction(action: Action) {
    action.redirectUrl ??= window.location.href;
    localStorage.setItem('action', JSON.stringify(action));
  }

  public static removeAction() {
    localStorage.removeItem('action');
  }

  public static extractLearningExperienceId(exps: Record[]) {
    if (exps == null || exps.length === 0) {
      return null;
    }

    return exps[0].learningExperienceId;
  }

  public static runAction(user: User, playerUrlAnnotationService: PlayerUrlAnnotationService, record: Record) {
    const action: Action = Util.getAction();

    if (action && action.type === ActionType.OPEN_ANNOTATION && user) {
      playerUrlAnnotationService
        .getPlayerUrlAnnotation(action.recordId, action.annotationId)
        .subscribe((url) => {
          location.href = url;
        });
      return;
    }

    if (action && user && action.type !== ActionType.NONE) {
      const selectors = {
        [ActionType.ADD_FAVORITE]: `#favoriteButton${action.recordId}`,
        [ActionType.OPEN_EXTERNAL]: '#externalLinkButton',
        [ActionType.ADD_TO_LIST]: '#addToListButton',
        [ActionType.CHECKOUT]: `#checkoutButton${action.recordId}`,
        [ActionType.DOWNLOAD]: '#attachedResourceButton',
        [ActionType.FOLLOW]: `#followButton${action.recordId}`,
        [ActionType.HOLD]: `#holdButton`,
      };

      const button: HTMLButtonElement = document.querySelector(selectors[action.type]);
      if (button) {
        button.click();
      }
    }
    if (user) {
      Util.removeAction();
    } else {
      if (!action || action.type !== ActionType.OPEN_ANNOTATION) {
        Util.setAction({
          type: ActionType.NONE,
          recordId: record.id,
        });
      }
    }
  }

  public static getPagesPerHour(pages: number, time: number) {
    return time === 0 ? NaN : Util.roundToNDecimalPlaces((pages * 3600000) / time, 3);
  }

  public static computeChallengeProgressPercentage(c: Challenge) {
    const target = c.target;
    c.progress = isNaN(c.progress) ? 0 : c.progress;
    const progressPctg = Math.floor((100 * c.progress) / target);
    return progressPctg < 100 ? progressPctg : 100;
  }

  public static formatTime(time: number) {
    let seconds = Math.floor(time / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    seconds = seconds % 60;
    minutes = minutes % 60;

    return `${Util.padTo2Digits(hours)}:${Util.padTo2Digits(minutes)}:${Util.padTo2Digits(seconds)}`;
  }

  public static padTo2Digits(num) {
    return num.toString().padStart(2, '0');
  }

  private static resolveCover(record: Record, original?: boolean) {
    let cover = original ? record.originalImageUrl : record.coverImageUrl;

    if (!cover || !cover.trim()) {
      if (original) {
        return this.resolveCover(record);
      }

      return '/assets/cover.jpg';
    }

    if (cover.startsWith('public/')) {
      cover = 'https://covers.odilo.io/' + cover;
    }

    if (/\%/i.test(cover)) {
      return cover;
    }

    return encodeURI(cover);
  }

  private static resolveElectronicLocations(locations: ElectronicLocation[]): ElectronicLocation[] {
    if (!locations) {
      return null;
    }

    // legacy
    if (!Array.isArray(locations)) {
      locations = [locations];
    }

    locations.forEach((l) => (l.label = l.note || l.uri));
    return locations;
  }

  public static split(string: string, delimiter: string, limit: number, join = ' '): string[] {
    return [string.split(delimiter, limit).toString(), string.split(delimiter).slice(limit).join(join)];
  }

  private static createDummyRecord(type: string): Record {
    return {
      id: null,
      resourceTypes: [type],
    };
  }

  public static createDummyRecordFromList(title: string): Record {
    return {
      id: null,
      title: title,
    };
  }

  public static ensureTrailingSlash(str: string): string {
    if (str && !str.endsWith('/')) {
      str += '/';
    }

    return str;
  }

  public static roundToNDecimalPlaces(x: number, n: number) {
    return Math.round((x + Number.EPSILON) * 10 ** n) / 10 ** n;
  }

  public static unCamelCase(str: string): string {
    return str.replace(/([A-Z])/g, ' $1').replace(/^./, (_str) => _str.toUpperCase());
  }

  public static truncate(text: string, maxChars: number) {
    return text.substr(0, Math.min(text.length, maxChars > 0 ? maxChars : 0));
  }

  public static fromAnyObjectToMap(anyObject: any) {
    const map = new Map();
    Object.keys(anyObject).forEach((key) => {
      map.set(key, anyObject[key]);
    });
    return map;
  }

  public static isEmptyString(s: string) {
    return s == null || s.trim().length === 0;
  }

  public static getCurrentRoute(router: Router): Promise<string> {
    return new Promise((resolve, reject) => {
      router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          resolve(event.url);
        }
      });
    });
  }

  public static getAvailableExperiencesInfoFromFormatFilter(formatFilter: Filter): InfoDialogData {
    const availableExperiencesSimplifiedNames = formatFilter.values
      .filter((filterValue) => Util.isLearningExperienceFilterValue(filterValue))
      .map((filterValue) => Util.toLowerCaseWithoutSpaces(filterValue.name));
    const experiencesInfo = experiencesDescriptions
      .filter((desc) => availableExperiencesSimplifiedNames.includes(Util.toLowerCaseWithoutSpaces(desc.name)))
      .sort((a, b) => this.alphabeticSortCompareFn(a.name, b.name));
    return {
      title: 'search.learningExperiences',
      intro: 'experiences.info.intro',
      items: experiencesInfo.map((experienceInfo) => {
        const item: InfoDialogItem = {
          title: experienceInfo.name,
          description: experienceInfo.description,
          iconLigature: experienceInfo.iconLigature,
        };
        return item;
      }),
    };
  }

  public static setCookie(name: string, value: string, days: number) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/; secure';
  }
  public static getCookie(name: string) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let c of ca) {
      while (c.charAt(0) == ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) == 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }

  public static isLearningExperienceFilterValue(filterValue: FilterValue) {
    return filterValue.iconId != null && filterValue.iconId.startsWith('le');
  }

  public static isPhysicalFilterValue(filterValue: FilterValue) {
    return this.ORIGINAL_FORMATS.includes(filterValue.name);
  }

  public static toLowerCaseWithoutSpaces(s: string) {
    return s.toLowerCase().replace(/\s/g, '');
  }

  public static alphabeticSortCompareFn(a: string, b: string) {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  }
}

export interface PaginatedResult<T> {
  itemsPerPage: number;
  numPages: number;
  itemsTotal: number;
  page: number;
  items: Array<T>;
}
